import { ChangeDetectionStrategy, Component, EventEmitter, inject } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'

import { TooltipModule } from 'primeng/tooltip'

import { map } from 'rxjs'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import {
  buildResourceBreadcrumb,
  getResourceDisplayName,
} from '@flowx/designer/shared/utils/application-resource.utils'

import { FlxNotificationContextComponent } from '../notification-context/notification-context.component'
import { FlxNotificationSummaryComponent } from '../notification-summary/notification-summary.component'
import { NotificationsCenterApi } from '../notifications-center/notifications-center.api'
import { ExportNotification } from '../notifications.model'

export enum ExportReviewOption {
  DOWNLOAD = 'DOWNLOAD',
  CHANGE_TARGET_VERSION = 'CHANGE_TARGET_VERSION',
}

export interface ExportReviewModalData {
  notification: ExportNotification
}

/**
 * Shows what an export left out before the archive is downloaded. It only reports the choice back
 * through `dataEventEmitter`; the opener acts on it and closes the modal.
 */
@Component({
  selector: 'flx-export-review-modal',
  imports: [
    TooltipModule,
    FlxButtonModule,
    FlxModalModule,
    FlxSpinnerModule,
    FlxNotificationContextComponent,
    FlxNotificationSummaryComponent,
  ],
  templateUrl: './export-review-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxExportReviewModalComponent {
  #modalService = inject(FlxModalService)
  #notificationsCenterApi = inject(NotificationsCenterApi)
  #data = inject<ExportReviewModalData>(FLX_MODAL_DATA)

  ExportReviewOption = ExportReviewOption

  dataEventEmitter = new EventEmitter<ExportReviewOption>()

  notification = this.#data.notification
  targetVersion = this.#data.notification.payload.flowxVersion

  excludedResources = rxResource({
    defaultValue: [],
    stream: () =>
      this.#notificationsCenterApi.getNotificationDetails(this.notification.id).pipe(
        map((details) =>
          (details?.resources ?? []).map(({ resourceType, resourceName }) => ({
            breadcrumb: buildResourceBreadcrumb(resourceType),
            name: getResourceDisplayName(resourceType, resourceName),
          }))
        )
      ),
  })

  // Neither choice runs until the request for the list has settled, whatever it returned; the spinner shows the wait.
  onActionClick(option: ExportReviewOption): void {
    if (!this.excludedResources.isLoading()) {
      this.dataEventEmitter.emit(option)
    }
  }

  close(): void {
    this.#modalService.hide()
  }
}
